import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApartmentOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LikeOutlined,
  RightOutlined,
  SendOutlined,
  ShareAltOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Swal from 'sweetalert2';
import { route } from '../../../../../constants/routes';
import { useJobStore } from '../../../../../core/store/job.store';
import JobDescription from '../../../../public/components/job-details-preview/JobDescription';
import '../../../../public/components/job-details-preview/job-detail-preview.css';
import '../../../../public/components/job-details-preview/quill-rich-content.css';
import './JobPreview.css';
import { Job } from '../models/job.model';
import { JobService } from '../services/job.service';

/**
 * Recruiter-facing preview of the public job listing layout.
 *
 * Uses draft store data (unsaved form) or a loaded job by id. Interactive
 * candidate actions stay disabled so recruiters see the candidate experience
 * without applying, liking, or running Job Fit from admin.
 *
 * @example Opened from JobForm via `/job/preview` or `/job/:id/preview`.
 */
const JobPreview: React.FC = () => {
  const [job, setJob] = useState<Job>(new Job());
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id?: string }>();
  const stored = useJobStore.getState().getJob();
  const navigate = useNavigate();

  const initializePreview = useCallback(async () => {
    try {
      window.scrollTo?.(0, 0);

      if (!id && !stored) {
        navigate(route('/job'));
        return;
      }

      if (id) {
        const loadedJob = await JobService.GetJob(id);
        setJob(loadedJob);
      } else if (stored) {
        const now = new Date();
        setJob({
          ...stored,
          postedDate: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(now.getDate()).padStart(2, '0')}`,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('SHOW: ')) {
        navigate(route('/job'));
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'info',
          title: message.slice(5, message.length),
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, stored]);

  useEffect(() => {
    void initializePreview();
  }, [initializePreview]);

  const locationLabel =
    [job.location, job?.countries?.join(' / ')].filter(Boolean).join(' - ') ||
    'Location flexible';

  const categoryTags = [job.category, job.subCategory].filter(Boolean);
  const applyLabel = job.featured ? 'Easy Apply' : 'Apply now';

  return (
    <main className="public-page-shell job-preview-shell w-full">
      {loading && (
        <div className="spinner-container" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span className="sr-only">Loading job preview</span>
        </div>
      )}

      <div className="public-content-rail ats-stack">
        <p className="job-preview-banner" role="status">
          Candidate listing preview — Apply, Job Fit, and social actions are
          disabled here.
        </p>

        <section className="public-panel">
          <div className="public-panel-header">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="public-eyebrow flex items-center gap-2">
                  <ApartmentOutlined />
                  {job.company || 'Company'}
                </div>
                <h1 className="jo-heading m-0 break-words text-2xl font-black leading-tight sm:text-3xl lg:text-4xl">
                  {job.name || 'Job title'}
                </h1>
              </div>

              <button
                type="button"
                className="public-button-primary job-detail-apply job-detail-apply-header w-full sm:w-auto"
                disabled
                title="Disabled in recruiter preview"
              >
                <SendOutlined />
                {applyLabel}
              </button>
            </div>
          </div>

          <div className="public-panel-body ats-stack">
            <div className="ats-meta-grid ats-meta-grid--detail">
              <span className="ats-meta-item">
                <EnvironmentOutlined />
                <span className="truncate">{locationLabel}</span>
              </span>
              <span className="ats-meta-item ats-meta-item--end">
                <CalendarOutlined />
                <span className="truncate">{job.postedDate || '—'}</span>
              </span>
            </div>

            {categoryTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {categoryTags.map((tag) => (
                  <span key={tag} className="ats-category-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {job.closed && <span className="jo-status-closed">Closed</span>}
              {job.skills?.map((skill) => (
                <span
                  key={skill.id || skill.skillName}
                  className="ats-skill-chip"
                >
                  {skill.skillName}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[1fr_260px]">
          <div className="public-panel min-w-0">
            <div className="public-panel-body">
              <h2 className="jo-heading mb-4 text-xl font-black">
                Job Description
              </h2>
              <JobDescription
                description={job.description}
                renderAsHtml={job.featured}
              />
              <div className="job-detail-actions">
                <button
                  type="button"
                  className="public-button-secondary job-detail-back"
                  disabled
                  title="Disabled in recruiter preview"
                >
                  Back to Jobs
                </button>
                <button
                  type="button"
                  className="public-button-primary job-detail-apply"
                  disabled
                  title="Disabled in recruiter preview"
                >
                  <SendOutlined />
                  {applyLabel}
                </button>
              </div>
            </div>
          </div>

          <aside className="job-detail-sidebar ats-stack min-w-0">
            <section
              className="public-panel job-fit-card job-fit-teaser"
              aria-labelledby="job-fit-preview-heading"
            >
              <div className="public-panel-body ats-stack">
                <div>
                  <p className="public-eyebrow mb-1">Resume fit</p>
                  <h2
                    id="job-fit-preview-heading"
                    className="jo-heading m-0 text-lg font-black"
                  >
                    Check your fit
                  </h2>
                  <p className="jo-body-muted mb-0 mt-2 text-sm">
                    See how your resume aligns with{' '}
                    <span className="font-semibold text-[var(--jo-text)]">
                      {job.name || 'this role'}
                    </span>
                    .
                  </p>
                </div>
                <button
                  type="button"
                  className="public-button-primary w-full job-fit-teaser-cta"
                  disabled
                  title="Disabled in recruiter preview"
                >
                  Check your fit
                  <RightOutlined />
                </button>
              </div>
            </section>

            <div className="public-panel">
              <div className="public-panel-body ats-stack">
                <button
                  type="button"
                  className="public-button-secondary px-4"
                  disabled
                  title="Disabled in recruiter preview"
                >
                  <LikeOutlined />
                  0
                </button>
                <div className="public-button-secondary px-4">
                  <EyeOutlined />
                  0
                </div>
                {job.featured && (
                  <div
                    className="public-button-secondary px-4"
                    aria-label={`${job.jobApplicationsSize || 0} applicants`}
                  >
                    <TeamOutlined />
                    {job.jobApplicationsSize || 0}{' '}
                    {(job.jobApplicationsSize || 0) === 1
                      ? 'applicant'
                      : 'applicants'}
                  </div>
                )}
                <button
                  type="button"
                  className="public-button-secondary px-4"
                  disabled
                  title="Disabled in recruiter preview"
                >
                  <ShareAltOutlined />
                  Share
                </button>
              </div>
            </div>
          </aside>
        </section>
      </div>
    </main>
  );
};

export default JobPreview;
